import { useState, useCallback } from "react";
import i18n from "../../../../../i18n";
import { getDefaultQuestionPatch, QuizDraftQuestion, QuizQuestionType } from "../verityAssessmentHelper";
import { QuizQuestionInput, VerityAssessment } from "@verity/api-client/types";

function getDefaultQuestion(): QuizDraftQuestion {
	return { questionType: null, questionText: "", questionOrder: 1, correctionPoints: 1 };
}

export function buildInitialQuestions(verityAssessment?: VerityAssessment): QuizQuestionInput[] | undefined {
	const questions = verityAssessment?.questions as QuizQuestionInput[] | undefined;
	if (!questions || questions.length === 0) return undefined;
	return questions;
}

export default function useQuizQuestions(initialQuestions?: QuizDraftQuestion[]) {
	const [questions, setQuestions] = useState<QuizDraftQuestion[]>(
		initialQuestions && initialQuestions.length > 0 ? initialQuestions : [getDefaultQuestion()],
	);
	const [selectedIndex, setSelectedIndex] = useState<number>(0);
	const baseTransKey = "portal.components.verityAssessmentTSX";

	const onAddQuestion = useCallback(() => {
		setQuestions((prev) => {
			const updated = [
				...prev,
				{ questionType: null, questionText: "", questionOrder: prev.length + 1, correctionPoints: 1 },
			];
			setSelectedIndex(updated.length - 1);
			return updated;
		});
	}, []);

	const onDuplicateQuestion = useCallback((index: number) => {
		setQuestions((prev) => {
			const updated = [...prev.slice(0, index + 1), { ...prev[index] }, ...prev.slice(index + 1)].map((q, i) => ({
				...q,
				questionOrder: i + 1,
			}));
			setSelectedIndex(index + 1);
			return updated;
		});
	}, []);

	const onRemoveQuestion = useCallback((index: number) => {
		setQuestions((prev) => {
			const filtered = prev.filter((_, i) => i !== index);
			if (filtered.length === 0) {
				setSelectedIndex(0);
				return [{ questionType: null, questionText: "", questionOrder: 1, correctionPoints: 1 }];
			}
			const updated = filtered.map((q, i) => ({ ...q, questionOrder: i + 1 }));
			setSelectedIndex((sel) => {
				if (index <= sel) return Math.max(0, sel - 1);
				return sel;
			});
			return updated;
		});
	}, []);

	const onSelectType = useCallback(
		(type: QuizQuestionType) => {
			setQuestions((prev) =>
				prev.map((q, i) =>
					i === selectedIndex
						? {
								...q,
								questionType: type,
								questionText: i18n.t(`${baseTransKey}.questions.questionTextPlaceholder`, {
									quesionIdx: i + 1,
								}) as string,
								...getDefaultQuestionPatch(type),
							}
						: q,
				),
			);
		},
		[selectedIndex],
	);

	const onSelectedQuestionChange = useCallback(
		(updated: QuizDraftQuestion) => {
			setQuestions((prev) => prev.map((q, i) => (i === selectedIndex ? updated : q)));
		},
		[selectedIndex],
	);

	return {
		questions,
		selectedIndex,
		setSelectedIndex,
		onQuestionsChange: setQuestions,
		onAddQuestion,
		onDuplicateQuestion,
		onRemoveQuestion,
		onSelectType,
		onSelectedQuestionChange,
	};
}

export type QuizQuestionsReturn = ReturnType<typeof useQuizQuestions>;
